import {
  CodeSnippet,
  ComposedModal,
  InlineNotification,
  ModalBody,
  ModalHeader,
  StructuredListBody,
  StructuredListCell,
  StructuredListRow,
  StructuredListWrapper,
} from '@carbon/react';
import { asProblem, problemMessage } from '@pos/admin-ui';
import { useTranslation } from '@pos/i18n';
import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';

/** A log record's fields and what it carried, e.g. the row a store sent. Read-only. */
export function LogDetail({
  title,
  label,
  fields,
  queryKey,
  loadPayload,
  payload,
  onClose,
}: {
  title: string;
  label: string;
  fields: [string, ReactNode][];
  queryKey?: readonly unknown[];
  /** Loads what the record carried, when the list doesn't have it. */
  loadPayload?(): Promise<unknown>;
  payload?: unknown;
  onClose(): void;
}) {
  const { t } = useTranslation();
  const loaded = useQuery({
    queryKey: queryKey ?? ['none'],
    queryFn: () => loadPayload?.() ?? null,
    enabled: Boolean(loadPayload),
  });
  const content = loadPayload ? loaded.data : payload;
  return (
    <ComposedModal open size="lg" onClose={onClose}>
      <ModalHeader title={title} label={label} iconDescription={t('carbon.close')} />
      <ModalBody>
        <StructuredListWrapper isCondensed aria-label={title}>
          <StructuredListBody>
            {fields.map(([name, value]) => (
              <StructuredListRow key={name}>
                <StructuredListCell noWrap>{name}</StructuredListCell>
                <StructuredListCell className="pos-log__value">{value ?? '—'}</StructuredListCell>
              </StructuredListRow>
            ))}
          </StructuredListBody>
        </StructuredListWrapper>
        {loaded.error && (
          <InlineNotification
            kind="error"
            lowContrast
            hideCloseButton
            title={problemMessage(t, asProblem(loaded.error))}
          />
        )}
        {content !== undefined && content !== null && (
          <>
            <h3 className="pos-log__payload-title">{t('logs.payload')}</h3>
            <CodeSnippet
              type="multi"
              feedback={t('logs.copied')}
              copyButtonDescription={t('logs.copy')}
              showMoreText={t('logs.showMore')}
              showLessText={t('logs.showLess')}
            >
              {JSON.stringify(content, null, 2)}
            </CodeSnippet>
          </>
        )}
      </ModalBody>
    </ComposedModal>
  );
}
